import React from 'react';
import {Divider, List, PageContent, Preloader, Typography} from '@genestack/ui';
import {Sidebar} from '../../../../components/layout/sidebar';
import {SidebarHeader} from '../../../../components/layout/sidebar/sidebar-header/sidebar-header';
import {useMetadataQuery} from './hooks/use-metadata-query';
import {DocumentTypeLabel} from '../../../../components/document-type-label';
import {DocumentSources} from './document-sources';
import {MetadataEntry} from './metadata-entry';
import {getFormattedDate} from '../../../../utils/format-date';
import {metadataArrayToString} from './helpers';
import {TagsList} from '../tags/tags-list';
import {TagsInput} from '../tags/tags-input';

interface EditModeProps {
    document: EditModeDoc;
    onClose: () => void;
}

interface ViewModeProps {
    document: ViewModeDoc;
    onClose: () => void;
    globalVersion: number;
}

function isViewModeProps(props: EditModeProps | ViewModeProps): props is ViewModeProps {
    return 'globalVersion' in props;
}

export function MetadataSidebar(props: EditModeProps | ViewModeProps) {
    const isViewMode = isViewModeProps(props);
    const isEditMode = !isViewMode;
    const metadataQuery = useMetadataQuery(
        props.document?.accession,
        isViewMode ? props.globalVersion : undefined
    );

    return (
        <Sidebar border="left">
            <SidebarHeader title="Metadata" onClose={props.onClose} />

            {(function () {
                if (!metadataQuery.data) {
                    return (
                        <PageContent>
                            <Preloader show count={5} box="paragraph" />
                        </PageContent>
                    );
                }

                return (
                    <List>
                        <MetadataEntry label="Type: ">
                            <DocumentTypeLabel
                                type={props.document.type}
                                readOnly={props.document.readOnly}
                            />
                        </MetadataEntry>

                        {props.document.type === 'BTB' && (
                            <MetadataEntry label="Categories: ">
                                {!metadataQuery.data.categories.length
                                    ? 'Uncategorized'
                                    : metadataArrayToString(metadataQuery.data.categories)}
                            </MetadataEntry>
                        )}

                        {props.document.type === 'BTB' && (
                            <MetadataEntry label="Taxon(s): ">
                                {!metadataQuery.data.taxons.length
                                    ? 'Unspecified'
                                    : metadataArrayToString(metadataQuery.data.taxons.sort())}
                            </MetadataEntry>
                        )}

                        {props.document.type === 'BTB' && (
                            <DocumentSources sources={metadataQuery.data.publicationSources} />
                        )}

                        {isEditMode && (
                            <MetadataEntry label="Current status: ">
                                {(function () {
                                    if (props.document.status === 'DELETED') {
                                        return 'Deleted';
                                    }

                                    if (props.document.status === 'PUBLISHED') {
                                        return 'Published';
                                    }

                                    return 'Draft';
                                })()}
                            </MetadataEntry>
                        )}

                        {isEditMode && props.document.status === 'PUBLISHED' && (
                            <MetadataEntry label="Published by: ">
                                {metadataQuery.data.publishedBy}
                            </MetadataEntry>
                        )}

                        {isEditMode && props.document.status === 'PUBLISHED' && (
                            <MetadataEntry label="Published on: ">
                                {getFormattedDate({date: metadataQuery.data.publishedAt!})}
                            </MetadataEntry>
                        )}

                        {isEditMode && props.document.status === 'DELETED' && (
                            <MetadataEntry label="Deleted by: ">
                                {metadataQuery.data.deletedBy}
                            </MetadataEntry>
                        )}

                        {isEditMode && props.document.status === 'DELETED' && (
                            <MetadataEntry label="Deleted on: ">
                                {getFormattedDate({date: metadataQuery.data.deletedAt!})}
                            </MetadataEntry>
                        )}

                        <MetadataEntry label="Created on: ">
                            {getFormattedDate({date: metadataQuery.data.createdAt})}
                        </MetadataEntry>

                        <MetadataEntry
                            label={isViewMode ? 'Valid in selected GV: ' : 'Valid in latest GV: '}
                        >
                            {(function () {
                                if (metadataQuery.data.validity === 'VALID') {
                                    return 'Yes';
                                }

                                if (metadataQuery.data.validity === 'NOT_VALIDATED') {
                                    return 'Not validated';
                                }

                                return 'No';
                            })()}
                        </MetadataEntry>

                        <MetadataEntry label="Imported: ">
                            {metadataQuery.data.isImported ? 'Yes' : 'No'}
                        </MetadataEntry>

                        <MetadataEntry label="Editable: ">
                            {metadataQuery.data.isEditable ? 'Yes' : 'No'}
                        </MetadataEntry>

                        <MetadataEntry label="Created by: ">
                            {metadataQuery.data.creator}
                        </MetadataEntry>

                        {isEditMode && metadataQuery.data.isEditable && (
                            <MetadataEntry label="Editor(s): ">
                                {metadataArrayToString(metadataQuery.data.editors.sort())}
                            </MetadataEntry>
                        )}

                        {isEditMode && metadataQuery.data.status === 'DRAFT' && (
                            <MetadataEntry label="Last modified on: ">
                                {getFormattedDate({date: metadataQuery.data.lastModifiedAt})}
                            </MetadataEntry>
                        )}

                        {metadataQuery.data.isEditable && (
                            <MetadataEntry label="GV of last update: ">
                                {metadataQuery.data.globalVersionLabelOfLastUpdate}
                            </MetadataEntry>
                        )}

                        {isEditMode && metadataQuery.data.isEditable && (
                            <MetadataEntry label="Changes since last GV: ">
                                {metadataQuery.data.hasUnversionedChanges ? 'Yes' : 'No'}
                            </MetadataEntry>
                        )}
                    </List>
                );
            })()}

            {isEditMode && (
                <React.Fragment>
                    <PageContent>
                        <Typography variant="section">Public tags</Typography>
                        <Divider variant="transparent" />
                        <TagsList
                            tagsType="public"
                            documentAccession={props.document.accession}
                            emptyComponent={
                                <Typography intent="quiet">No public tags added</Typography>
                            }
                            removableTags
                        />
                        <Divider variant="transparent" />
                        <TagsInput isPublic docAccession={props.document.accession} />
                    </PageContent>

                    <PageContent>
                        <Typography variant="section">Private tags</Typography>
                        <Divider variant="transparent" />
                        <TagsList
                            tagsType="private"
                            documentAccession={props.document.accession}
                            emptyComponent={
                                <Typography intent="quiet">No private tags added</Typography>
                            }
                            removableTags
                        />
                        <Divider variant="transparent" />
                        <TagsInput isPublic={false} docAccession={props.document.accession} />
                    </PageContent>
                </React.Fragment>
            )}
        </Sidebar>
    );
}
